import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, useFrontier } from 'react-frontier2';
import { useTranslation } from 'react-i18next';
import { AssetFile, AssetRelations, AssetType } from '../AdminClasses';
import { mimeToAssetType } from '../AdminUtil';
import { DEBUG } from '../AdminConfig';
import API from '../API';

interface UploadAssetButtonProps{
	text?: string,
	maxSize?: number,
	size?: string,
	data: AssetRelations,
	typeId: AssetType | AssetType[],
	render?: (loading: boolean, onClick: ()=>void)=>any,
	onUpload: (file: AssetFile)=>void
}

var UploadAssetButton = (props: UploadAssetButtonProps)=>{
	var { t } = useTranslation();
	var { Toast } = useFrontier();
	var [loading, setLoading] = useState<boolean>(false);
	var inputRef = useRef<HTMLInputElement>(null);

	var onButtonClick = ()=>{
		if(inputRef.current){
			inputRef.current.click();
		}
	}

	var onFileUpload = (event: React.ChangeEvent<HTMLInputElement>)=>{
		var files = event.target.files;
		if(!files || !files[0]) return;
		if(files.length>1) return Toast.error(t('toasts.one_file'));

		var file_size = (props.maxSize || (2 * 1024 * 1024));
		if(files[0].size > file_size) return Toast.error(t('toasts.file_size', { size: `${Math.floor(file_size / 1024 / 1024)}MB` }));
		setLoading(true);

		var type_id = mimeToAssetType(files[0].type);
		if(!type_id) return Toast.error(t('toasts.invalid_file_type'));

		API.uploadAsset(files[0], type_id, props.data).then(res=>{
			if(res.error) return Toast.error(res.message);
			props.onUpload(res.data);
		}).catch(err=>{
			if(DEBUG) console.log(err);
			return Toast.error(t('common.unexpected_code', { code: 'LCL-UPLASTBTN-1' }));
		}).finally(()=>{
			setLoading(false);
		})
	}

	var typeToMime = (type: AssetType)=>{
		switch(type){
			case AssetType.IMAGE: return 'image/*';
			case AssetType.PDF: return 'application/pdf';
		}
	}

	var accept_type = useMemo(()=>{
		if(Array.isArray(props.typeId)){
			var acc = [];
			for(var i of props.typeId){
				acc.push(typeToMime(i));
			}
			return acc.join(',');
		}else{
			return typeToMime(props.typeId);
		}
	}, [props.typeId]);
	
	return <>
		{props.render ? (
			props.render(loading, onButtonClick)
		) : (
			<Button loading={loading} size={props.size as any} color='black' iconName='upload' text={props.text || t('common.upload_file')} onClick={onButtonClick} />
		)}
		<input ref={inputRef} onChange={onFileUpload} style={{ display: 'none' }} type="file" accept={accept_type} />
	</>
}

export default UploadAssetButton;